import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import theme from './theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './AuthContext';
import Landing from './pages/Landing';
import CreateAccount from './pages/CreateAccount';
import PrivateLogin from './pages/PrivateLogin';
import Profile from './pages/Profile';
import Discover from './pages/Discover';
import Conversations from './pages/Conversations';
import Manifesto from './pages/Manifesto';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PrivateRoute from './components/PrivateRoute';
import SmartRedirect from './components/SmartRedirect';
import InstallPrompt from './components/InstallPrompt';
import { ToastProvider } from './hooks/useToast';

const Admin = React.lazy(() => import('./pages/Admin'));
const Chat = React.lazy(() => import('./pages/Chat'));
const Feedback = React.lazy(() => import('./pages/Feedback'));
const ProductFeedback = React.lazy(() => import('./pages/ProductFeedback'));
// Redirect old KY routes to standalone app
const ExternalRedirect: React.FC<{ to: string }> = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});


const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <Router>
          <ScrollToTop />
          <AuthProvider>
            <ToastProvider>
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/create-account" element={<CreateAccount />} />
            <Route path="/u/:urlId" element={<PrivateLogin />} />
            <Route
              path="/home"
              element={
                <PrivateRoute>
                  <SmartRedirect />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/discover"
              element={
                <PrivateRoute>
                  <Discover />
                </PrivateRoute>
              }
            />
            <Route
              path="/conversations"
              element={
                <PrivateRoute>
                  <Conversations />
                </PrivateRoute>
              }
            />
            <Route
              path="/conversations/:matchId"
              element={
                <PrivateRoute>
                  <React.Suspense fallback={<Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>}>
                    <Chat />
                  </React.Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/conversations/:matchId/feedback"
              element={
                <PrivateRoute>
                  <React.Suspense fallback={<Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>}>
                    <Feedback />
                  </React.Suspense>
                </PrivateRoute>
              }
            />
            <Route path="/admin" element={
              <React.Suspense fallback={<Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>}>
                <Admin />
              </React.Suspense>
            } />
            <Route path="/product-feedback" element={
              <React.Suspense fallback={<Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>}>
                <ProductFeedback />
              </React.Suspense>
            } />
            <Route path="/know-yourself" element={<ExternalRedirect to="https://know-yourself.dateseriously.com" />} />
            <Route path="/know-yourself/results" element={<ExternalRedirect to="https://know-yourself.dateseriously.com/results" />} />
            <Route path="/know-yourself/shared/:token" element={<ExternalRedirect to="https://know-yourself.dateseriously.com" />} />
            <Route path="/manifesto" element={<Manifesto />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            </Routes>
            </ToastProvider>
            <InstallPrompt />
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
